<template>
  <div class="custom-form-item">
    <!-- text -->
    <template v-if="control._type === 'text'">
      <el-input
        v-model="editValue"
        v-ps="'textarea'"
        type="textarea"
        resize="none"
        :placeholder="control.hint"
        :disabled="disabled"
        @input="outputSlow"
        :autosize="{ minRows: 1, maxRows: 4 }"
        class="text-input"
      ></el-input>
    </template>
    <template v-else-if="control._type === 'telephone'">
      <el-input v-model="editValue" :disabled="disabled" @input="outputSlow">
        <country-code-selector
          class="country-code-selector"
          :countryCode.sync="prefix"
          slot="prepend"
        ></country-code-selector>
      </el-input>
    </template>
    <template v-else-if="control._type === 'mail'">
      <el-input
        v-model="editValue"
        :placeholder="control.hint"
        :disabled="disabled"
        @input="outputSlow"
      ></el-input>
    </template>
    <!-- 'number', 'money', 'calc', 'gather' -->
    <template v-else-if="['number', 'money', 'calc', 'gather'].includes(control._type)">
      <el-input
        type="number"
        v-model="editValue"
        :placeholder="control.hint"
        :disabled="disabled"
        :precision="control.decimal"
        @input="outputSlow"
        :class="[
          'has-unit',
          'unit-length-' + parseInt(strLen(control.unit) / 2),
          ['calc', 'gather'].includes(control._type) ? 'cursor-normal' : ''
        ]"
      >
        <span slot="suffix" class="unit" v-tip>&nbsp;{{ control.unit }}</span>
      </el-input>
    </template>
    <!--    str_calc-->
    <template v-else-if="['str_calc'].includes(control._type)">
      <el-input v-model="editValue" :disabled="disabled" @input="outputSlow"></el-input>
    </template>
    <!-- money_cn -->
    <template v-else-if="['money_cn'].includes(control._type)">
      <el-input
        type="textarea"
        :autosize="{ minRows: 1 }"
        v-model="editValue"
        :placeholder="control.hint"
        :disabled="disabled"
        :controls="false"
        :class="{ 'cursor-normal': true }"
        @input="output"
      ></el-input>
    </template>
    <!-- option -->
    <template v-else-if="['option'].includes(control._type)">
      <option-selector
        :control="control"
        :disabled="disabled"
        :form="form"
        v-model="editValue"
        @input="output"
      ></option-selector>
    </template>
    <!-- select2 -->
    <template v-else-if="control._type === 'select2'">
      <select2
        v-model="editValue"
        :control="control"
        sheetId="123"
        :disabled="disabled"
        @change="output($event)"
      ></select2>
    </template>
    <!-- rich -->
    <template v-else-if="control._type === 'rich'">
      <rich-input
        ref="rich"
        v-model="editValue"
        :disabled="disabled"
        @input="outputSlow"
        :type="type === 'table' ? 'dropdown' : 'common'"
        @fullscreen-state-changed="handleRichTextFullScreen"
      ></rich-input>
    </template>
    <!-- radio -->
    <template v-else-if="control._type === 'radio'">
      <el-radio-group v-model="editValue" :disabled="disabled" @change="output">
        <el-radio v-for="(group, index) in control.groups" :key="index" :label="group">
          {{ group }}
        </el-radio>
      </el-radio-group>
    </template>
    <!-- date, time -->
    <template v-else-if="['date', 'time'].includes(control._type)">
      <date-popover
        v-if="['半年度', '季度'].includes(control.timeType)"
        :date="editValue"
        :disabled="disabled"
        :placeholder="control.hint"
        @change="output"
        class="date-popover"
        :time-type="control.timeType"
      ></date-popover>
      <el-date-picker
        v-else
        v-model="editValue"
        :placeholder="control.hint"
        :disabled="disabled"
        :type="dateTimePicker.type"
        :value-format="dateTimePicker.valueFormat"
        :format="dateTimePicker.displayFormat"
        @input="output"
        :picker-options="dateTimePicker.pickerOptions"
      ></el-date-picker>
    </template>
    <!-- staff -->
    <template v-else-if="control._type == 'staff'">
      <user-selector
        v-model="editValue"
        :multiple="!control.oneFlag"
        :disabled="disabled"
        :provided-users="curUsers"
        status-normal
        @input="output"
      ></user-selector>
    </template>
    <!-- department -->
    <template v-else-if="control._type == 'department'">
      <department-selector
        v-model="editValue"
        :disabled="disabled"
        :multiple="!control.oneFlag"
        @input="output"
      ></department-selector>
    </template>
    <!-- company -->
    <template v-else-if="control._type == 'company'">
      <company-selector
        v-model="editValue"
        :scope="control.scope"
        :multiple="!control.oneFlag"
        :disabled="disabled"
        @input="output"
      ></company-selector>
    </template>
    <!-- address -->
    <template v-else-if="control._type === 'address'">
      <address-selector
        v-model="editValue"
        :level="control.level"
        :disabled="disabled"
        @change="output($event)"
      ></address-selector>
    </template>
    <!-- area -->
    <template v-else-if="control._type === 'area'">
      <address-selector
        v-model="editValue"
        :level="control.level"
        :disabled="disabled"
        :checkStrictly="!control.lastLevelOnly"
        :multiple="!control.oneFlag"
        @change="output($event)"
      ></address-selector>
    </template>
    <!-- location -->
    <template v-else-if="control._type === 'location'">
      <location-selector
        :key="control._cid"
        :disabled="disabled"
        :control="control"
        :form="form"
        v-model="editValue"
        @input="output"
      ></location-selector>
    </template>
    <!-- file -->
    <template v-else-if="control._type === 'file'">
      <upload-file
        ref="upload-file"
        :disabled="disabled"
        :oneFlag="oneFlag"
        @updateFiles="output($event)"
        :limited-file-suffix="control.fileType.length ? control.fileType : null"
      ></upload-file>
    </template>
    <!-- 'sn', 'field' -->
    <template v-else-if="['sn', 'field'].includes(control._type)">
      <div class="non-editable">{{ editValue }}</div>
    </template>
    <!-- remark -->
    <template v-else-if="control._type == 'remark'">
      <span v-viewer="{ backdrop: 'static' }">
        <span v-html="control.hint" class="rich-field"></span>
      </span>
    </template>
    <!-- rate -->
    <template v-else-if="control._type === 'rate'">
      <rate
        :disabled="control.disabled"
        :value="editValue"
        @output="output"
        :control="control"
      ></rate>
    </template>
    <!-- ref (oneFlag: true) -->
    <template v-else-if="control._type === 'ref'">
      <select-ref
        v-if="oneFlag"
        v-model="editValue"
        :key="control._cid"
        show-ref-data-dialog
        :disabled="disabled"
        :control="control"
        :sheet-data-server="sheetDataServer"
        @input="output"
        @refDataCreated="$emit('refDataCreated')"
      ></select-ref>
      <el-input
        v-else
        disabled
        :value="editValue"
        :autosize="{ minRows: 1, maxRows: 4 }"
        v-ps="'textarea'"
        type="textarea"
        resize="none"
      ></el-input>
    </template>
    <template v-else-if="control._type === 'sign'">
      <signature
        ref="signature"
        :placeholder="control.hint"
        :disabled="control.disabled"
        :value="editValue"
        class="signature"
        @output="output"
      ></signature>
    </template>
    <template v-else-if="control._type === 'code'">
      <div style="position: relative">
        <el-input v-model="editValue" :placeholder="control.hint" @input="setOutput"></el-input>
        <el-button
          v-show="show"
          class="code-verify"
          style="top: -3px;"
          type="text"
          @click="getCode"
        >
          获取验证码
        </el-button>
        <span class="code-verify" style="color: #1989FA" v-show="!show">{{ count }} s</span>
      </div>
    </template>
    <template v-else-if="control._type === 'check'">
      <el-checkbox
        v-model="editValue"
        :disabled="disabled"
        @change="doOutput"
        class="check"
      ></el-checkbox>
    </template>
    <template v-else-if="control._type === 'url'">
      <el-input v-model="editValue" :disabled="disabled" @input="outputSlow"></el-input>
    </template>
    <template v-else-if="control._type === 'progress'">
      <div class="progress">
        <el-progress
          :percentage="editValue && editValue >= 0 ? editValue : 0"
          :type="control.optionStyle === '条形' ? 'line' : 'circle'"
        ></el-progress>
        <el-button-group>
          <el-button
            v-if="control.reduce"
            :disabled="disabled"
            icon="el-icon-minus"
            @click="decrease"
          ></el-button>
          <el-button
            v-if="control.increase"
            :disabled="disabled"
            icon="el-icon-plus"
            @click="increase"
          ></el-button>
        </el-button-group>
      </div>
    </template>
  </div>
</template>

<script>
import { getIdList, hasOwnProperty } from "@/utils/common"
import AddressSelector from "@/components/AddressSelector"
import CompanySelector from "@/components/FormControl/CompanySelector"
import DepartmentSelector from "@/components/FormControl/DepartmentSelector"
import UploadFile from "./UploadFile"
import Select2 from "./Select2"
import SelectRef from "./SelectRef"
import RichInput from "@/components/FormControl/RichInput"
import Emitter from "@/mixins/emitter"
import UserSelector from "@/components/FormControl/UserSelector"
import LocationSelector from "@/components/FormControl/LocationSelector.vue"
import OptionSelector from "@/components/FormControl/OptionSelector.vue"
import { getUserControlScope } from "@/utils/sheet"
import { timeTypeList } from "@/views/Backend/AdminEdit/Sheet/Field/detail-setting-content/control-setting/static"
import dj from "dayjs"
import { strLen } from "@/utils/common"
import CountryCodeSelector from "@/components/FormControl/CountryCodeSelector"
import DatePopover from "@/components/BaseComponents/DatePopover"
import Signature from "@/components/BaseComponents/Signatue"
import Rate from "@/components/BaseComponents/Rate"
import { sendVerifyCode } from "@/api/sheet/sheetData"
import { getCompany, getDepartment, getUser } from "@/utils/user"

export default {
  name: "custom-form-item",
  components: {
    Signature,
    DatePopover,
    DepartmentSelector,
    UserSelector,
    AddressSelector,
    UploadFile,
    Select2,
    SelectRef,
    RichInput,
    CompanySelector,
    CountryCodeSelector,
    LocationSelector,
    OptionSelector,
    Rate
  },
  mixins: [Emitter],
  props: {
    form: Object,
    editable: Boolean,
    control: {
      type: Object,
      default: () => ({})
    },
    sheetDataServer: Object,
    allControlMap: Object,
    type: {
      type: String,
      default: "form" // form table
    }
  },
  watch: {
    prefix: function(newVal, oldVal) {
      this.doOutput(this.editValue)
    }
  },
  computed: {
    curUsers() {
      if (this.control._type !== "staff") return []
      return getUserControlScope(this.control, this.form, this.allControlMap)
    },
    disabled() {
      if (["calc", "gather", "money_cn", "str_calc"].includes(this.control._type)) return true
      return this.control.disabled || !this.editable
    },
    oneFlag() {
      return !(hasOwnProperty(this.control, "oneFlag") && !this.control.oneFlag)
    },
    dateTimePicker() {
      if (!["date", "time"].includes(this.control._type)) return {}
      if (this.control._type === "date")
        return { type: "date", valueFormat: "yyyy-MM-dd", displayFormat: "yyyy-MM-dd" }
      const timeTypeMap = this._.keyBy(timeTypeList, "value")
      let timeTypeItem = timeTypeMap[this.control.timeType]
      const isSameOrBefore = require("dayjs/plugin/isSameOrBefore")
      const isSameOrAfter = require("dayjs/plugin/isSameOrAfter")
      dj.extend(isSameOrBefore)
      dj.extend(isSameOrAfter)
      return {
        type: timeTypeItem.controlType,
        // valueFormat: "yyyy-MM-dd HH:mm:ss", // value的格式
        valueFormat: timeTypeItem.displayFormat, // value的格式
        djFormat: timeTypeItem.djFormat, // dayjs的格式
        displayFormat: timeTypeItem.displayFormat, // 日期输入框实际展示的格式
        pickerOptions: {
          selectableRange: this.control.timeScope.timeRange?.join(" - "),
          format: timeTypeItem.timeFormat, // 当控件选择时间时,时间的格式
          disabledDate: date => {
            if (
              !this._.isNumber(this.control.timeScope?.weekLimit) &&
              !this._.isArray(this.control.timeScope?.dateRange)
            )
              return false
            let dateResult =
              this._.keys(this.control.timeScope).includes("dateRange") &&
              !(
                this.control.timeScope.dateRange &&
                (this.control.timeScope.dateRange.includes("Now")
                  ? this.control.timeScope.dateRange[0] == null
                    ? dj(date).isSameOrBefore(new Date(), "date")
                    : dj(date).isSameOrAfter(new Date(), "date")
                  : dj(date).isSameOrAfter(new Date(this.control.timeScope.dateRange[0]), "date") &&
                    dj(date).isSameOrBefore(new Date(this.control.timeScope.dateRange[1]), "date"))
              )
            let weekResult =
              this._.keys(this.control.timeScope).includes("weekLimit") &&
              !(this.control.timeScope?.weekLimit & (1 << this.getISOWeekOfDate(date)))
            return dateResult || weekResult
          }
        }
      }
    }
  },
  created() {
    this.output = this._.debounce(this.doOutput, 100)
    this.outputSlow = this._.debounce(this.doOutput, 300)
  },
  data() {
    return {
      editValue: null,
      rate: 0,
      cellOption: "cellOption",
      connectedCompanies: null,
      output: null,
      outputSlow: null, // 更新比较频繁的控件使用
      strLen,
      prefix: 86,
      token: "",
      show: true,
      count: "",
      timer: null
    }
  },
  methods: {
    init(value) {
      switch (this.control._type) {
        case "file":
          this.$refs["upload-file"].init(value)
          break
        case "staff":
        case "department":
        case "company": {
          this.editValue = getIdList(value)
          break
        }
        case "area": {
          let thisValue = (value || []).map(item => item.split("-").slice(0, this.control.level))
          this.editValue = this.control.oneFlag ? thisValue[0] || [] : thisValue
          break
        }
        case "time":
          if (["半年度", "季度"].includes(this.control.timeType)) {
            this.editValue = value ? value : ""
          } else {
            this.editValue = value
              ? dj(value, "YYYY-MM-DD HH:mm:ss").format(this.dateTimePicker.djFormat)
              : ""
          }
          break
        case "telephone":
          if (!value) {
            this.prefix = 86
            this.editValue = ""
          } else {
            var text = value.replace("+", "").split(" ")
            if (text.length < 2) {
              this.prefix = 86
              this.editValue = Number(text[0]) || ""
            } else {
              this.editValue = text[1] || ""
              this.prefix = Number(text[0]) || 86
            }
          }
          break
        case "progress":
          if (value !== undefined) this.editValue = parseInt(value)
          else this.editValue = 0
          break
        case "option":
          if (this._.isArray(value)) this.editValue = this._.cloneDeep(value)
          else this.editValue = []
          break
        case "ref":
          if (this.control.isField && !this.oneFlag && this._.isArray(value)) {
            // 关联表关联多条展示
            this.editValue = value.map(t => t.title).join(",")
          } else {
            this.editValue = value
          }
          break
        default:
          this.editValue = this._.cloneDeep(value)
          break
      }
    },
    doOutput(newVal = null) {
      this.editValue = newVal
      if (this.control._dataType === "number" && this._.isString(newVal) && newVal.length === 0) {
        this.emitVal(null)
        return
      }
      switch (this.control._type) {
        case "area":
          if (newVal == null || newVal.length == 0) {
            this.emitVal([])
          } else {
            this.emitVal(
              (this.control.oneFlag ? [newVal] : newVal).map(addrArr => addrArr.join("-"))
            )
          }
          break
        case "time":
          this.emitVal(
            newVal ? dj(newVal, this.dateTimePicker.djFormat).format("YYYY-MM-DD HH:mm:ss") : ""
          )
          break
        case "telephone":
          if (this.prefix !== 86) {
            newVal = "+" + this.prefix + " " + newVal
          }
          this.emitVal(newVal)
          break
        case "staff":
          this.emitVal((newVal || []).map(getUser))
          break
        case "department":
          this.emitVal((newVal || []).map(getDepartment))
          break
        case "company":
          this.emitVal((newVal || []).map(getCompany))
          break
        default:
          this.emitVal(newVal)
      }
    },
    emitVal(val) {
      this.$emit("change", val)
    },
    handleRichTextFullScreen(state) {
      this.dispatch("edit-dialog", "changeNavMenuVisible", state)
    },
    optionClick(e, val) {
      if (e.target.tagName === "INPUT") return // 因为原生click事件会执行两次，第一次在label标签上，第二次在input标签上，故此处理
      if (val === this.editValue) {
        // 这里是因为点击的时候,el-radio的点击事件先进入此处,此处output引起的一系列处理完之后,才执行el-radio的内部逻辑
        // 会导致点击瞬间,这个el-radio的值被设置成空,然后el-radio处理点击的逻辑,判断此时是空,然后就把这个值重新设置到radio上
        // 所以加上延时可简单解决此问题
        setTimeout(() => this.output())
      }
    },
    getISOWeekOfDate(date) {
      // 周一:0 周二:1 ... 周日:6
      return (dj(date).day() - 1 + 7) % 7
    },
    setOutput(val) {
      this.$emit("change", { token: this.token, code: val })
    },
    getCode() {
      const TIME_COUNT = 60
      sendVerifyCode(this.control._cid, this.sheetDataServer.sheetId, this.form).then(res => {
        this.token = res
        if (!this.timer) {
          this.count = TIME_COUNT
          this.show = false
          this.timer = setInterval(() => {
            if (this.count > 0 && this.count <= TIME_COUNT) {
              this.count--
            } else {
              this.show = true
              clearInterval(this.timer)
              this.timer = null
            }
          }, 1000)
        }
      })
    },
    focus() {
      if (!this.disabled) {
        this.$children[0]?.focus && this.$children[0].focus()
      }
    },
    increase() {
      this.editValue += this.control.step
      if (this.editValue > 100) {
        this.editValue = 100
      }
      this.doOutput(this.editValue)
    },
    decrease() {
      this.editValue -= this.control.step
      if (this.editValue < 0) {
        this.editValue = 0
      }
      this.doOutput(this.editValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-form-item {
  line-height: 30px !important;
  .check {
    ::v-deep {
      .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner {
        background-color: #409eff;
        border-color: #409eff;
      }
      .el-checkbox__input.is-disabled.is-checked .el-checkbox__inner::after {
        border-color: #fff;
      }
      .el-checkbox__input.is-disabled .el-checkbox__inner {
        border-color: #dcdfe6;
        background-color: #fff;
      }
    }
  }
  .progress {
    display: flex;
    align-items: center;
    margin-top: 2px;
    .el-progress {
      ::v-deep {
        .el-progress-bar {
          margin-right: -50px;
        }
        .el-progress-circle {
          width: 78px !important;
          height: 78px !important;
        }
      }
    }
    .el-progress--line {
      flex: 1;
    }
    .el-progress--circle {
      width: 78px;
      margin-right: 10px;
    }
    .el-button {
      padding: 5px 5px;
    }
  }
  .code-verify {
    position: absolute;
    right: 10px;
  }
  ::v-deep {
    .el-date-editor,
    .el-select {
      width: 100%;
    }
    .cursor-normal.is-disabled .el-input__inner {
      cursor: default;
    }
    .el-cascader {
      line-height: 32px;
    }
    .el-input .el-input__inner {
      text-align: left;
    }
    .el-textarea__inner {
      resize: none;
    }
  }
  .text-input {
    ::v-deep {
      textarea {
        position: relative;
      }
    }
  }
  .dm-select {
    display: block;
  }
  .non-editable {
    height: 32px;
    padding: 6px 15px;
    background: #f5f7fa;
    border-radius: 4px;
    box-sizing: border-box;
    font-size: $font-size-main;
    color: $color-font-second;
    line-height: 1.4;
    border: 1px solid #dcdfe6;
    word-break: break-all;
    display: table;
    width: 100%;
  }
  .has-unit {
    &.unit-length-0 ::v-deep .el-input__inner {
      padding-right: 0;
    }
    &.unit-length-1 ::v-deep .el-input__inner {
      padding-right: 30px;
    }
    &.unit-length-2 ::v-deep .el-input__inner {
      padding-right: 45px;
    }
    &.unit-length-3 ::v-deep .el-input__inner {
      padding-right: 60px;
    }
    &.unit-length-4 ::v-deep .el-input__inner {
      padding-right: 75px;
    }
    &.unit-length-5 ::v-deep .el-input__inner {
      padding-right: 90px;
    }
    ::v-deep {
      .el-input__suffix {
        right: 12px;
        color: $color-font-title;
      }
      .el-input__inner {
        padding-right: 92px;
      }
    }
  }
  .address-selector {
    width: 100%;
  }
  ::v-deep .el-input-group__prepend {
    padding: 0px;
  }
  ::v-deep .country-code-selector {
    .selected-flag {
      height: 30px;
      .area-codeNum {
        color: #606266;
      }
    }
    .flag-list-box {
      background-color: white;
      width: 294px;
      padding-top: 5px;
      .search-input {
        height: 30px;
      }
    }
  }
  .rich-field {
    ::v-deep p {
      margin: 0;
    }
  }
  // 颜色
  .non-editable {
    color: #303133;
  }
  .el-textarea {
    ::v-deep {
      .el-textarea__inner {
        color: #303133;
      }
    }
  }
  .address-selector {
    ::v-deep {
      .el-input__inner {
        color: #303133;
      }
    }
  }
  .el-input {
    ::v-deep {
      .el-input__inner {
        color: #303133;
      }
    }
  }
  .sign-image {
    display: flex;
    flex-flow: column;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 1px;
    .delete-image {
      text-align: right;
      cursor: pointer;
    }
  }
}

.svg-icon_close-btn {
  &:hover {
    opacity: 0.4;
    filter: alpha(opacity=40);
  }
}

.overlong-style {
  line-height: 30px;
  .el-radio {
    margin-bottom: 10px;
  }
}

.unit {
  max-width: 80px;
}
.date-popover {
  ::v-deep {
    .el-popover__reference-wrapper {
      .el-input--mini {
        font-size: 14px;
        .el-input__inner {
          color: #303133;
        }
      }
    }
  }
}
</style>

<style lang="scss">
//没找到写到scoped中的办法
.el-date-picker {
  .el-picker-panel__footer {
    display: none;
  }
  .el-picker-panel__body-wrapper {
    .el-picker-panel__body {
      .el-picker-panel__content {
        .el-year-table {
          tbody {
            .current {
              .cell {
                background-color: #1989fa;
                border-radius: 3px;
                color: white;
              }
            }
          }
        }
        .el-month-table {
          tbody {
            .current {
              .cell {
                background-color: #1989fa;
                border-radius: 3px;
                color: white;
              }
            }
          }
        }
      }
    }
  }
}
</style>
